<script setup lang="ts">
import { useDebounce } from '@afinius/hooks/useDebounce';
import { useMutationObserver } from '@afinius/hooks/useMutationObserver';
import { useResizeObserver } from '@afinius/hooks/useResizeObserver';
import { classNames } from '@afinius/utilities/class-names';
import SimpleBar from 'simplebar-vue';
import { ComponentPublicInstance, computed, onMounted, ref } from 'vue';

import FadeMotion from '../FadeMotion/FadeMotion.vue';

interface Props {
  minHeight?: string;
  maxHeight?: string;
  minWidth?: string;
  isAbsolute?: boolean;
  hasForcedTopShadow?: boolean;
  shouldHideScrollBars?: boolean;
  axis?: 'x' | 'y' | 'both';
}

const props = withDefaults(defineProps<Props>(), {
  minHeight: undefined,
  maxHeight: undefined,
  minWidth: undefined,
  axis: 'both',
});

const overflowTolerance = 1;

const scrollable = ref<ComponentPublicInstance | null>(null);
const hasTopShadow = ref(false);
const hasBottomShadow = ref(false);
const hasLeftShadow = ref(false);
const hasRightShadow = ref(false);
const hasVerticalOverflow = ref(false);
const hasHorizontalOverflow = ref(false);

const element = computed<HTMLElement>(() =>
  scrollable.value?.$el.querySelector('.simplebar-content-wrapper'),
);

const handleShadows = useDebounce(() => {
  if (!element.value) return;

  const { scrollTop, scrollLeft, offsetHeight, offsetWidth, scrollHeight, scrollWidth } =
    element.value;

  const hasVerticalScrollBar =
    props.axis !== 'x' && scrollHeight - element.value.clientHeight > overflowTolerance;
  const hasHorizontalScrollBar =
    props.axis !== 'y' && scrollWidth - element.value.clientWidth > overflowTolerance;

  hasVerticalOverflow.value = hasVerticalScrollBar;
  hasHorizontalOverflow.value = hasHorizontalScrollBar;
  hasTopShadow.value = hasVerticalScrollBar && scrollTop > 0;
  hasBottomShadow.value =
    hasVerticalScrollBar &&
    scrollHeight - (offsetHeight + Math.ceil(scrollTop)) > overflowTolerance;
  hasLeftShadow.value = hasHorizontalScrollBar && scrollLeft > 0;
  hasRightShadow.value =
    hasHorizontalScrollBar &&
    scrollWidth - (offsetWidth + Math.ceil(scrollLeft)) > overflowTolerance;
});

useResizeObserver(element, handleShadows);
useMutationObserver(element, handleShadows);

onMounted(handleShadows);
</script>

<template>
  <div class="scrollable">
    <SimpleBar
      ref="scrollable"
      class="scrollable-content"
      :class="
        classNames({
          isAbsolute,
          shouldHideScrollBars,
          axis,
          hasNoVerticalOverflow: !hasVerticalOverflow,
          hasNoHorizontalOverflow: !hasHorizontalOverflow,
        })
      "
      :onScroll="handleShadows"
      :autoHide="false"
      :style="{ minHeight, maxHeight, minWidth }"
    >
      <slot />
    </SimpleBar>

    <FadeMotion>
      <div v-if="hasForcedTopShadow || hasTopShadow" class="scrollable-top-shadow" />
    </FadeMotion>
    <FadeMotion><div v-if="hasBottomShadow" class="scrollable-bottom-shadow" /></FadeMotion>
    <FadeMotion><div v-if="hasLeftShadow" class="scrollable-left-shadow" /></FadeMotion>
    <FadeMotion><div v-if="hasRightShadow" class="scrollable-right-shadow" /></FadeMotion>
  </div>
</template>

<style lang="scss" scoped>
@use './Scrollable.scss';
</style>
